import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React from 'react';
import * as Yup from 'yup';
import { type AutocompleteContents, AutocompleteInput } from '../../../../components/CustomFields/SimpleAutocomplete';
import { Dialog } from '../../../../components/Dialog';
import { WizardStep } from '../../../../components/Wizard/WizardStep';
import type { InputComponentProps, OnSubmit } from '../../../../design-system';
import { useWizard } from '../../../../design-system';
import type { ConfigurationKey } from '../../../../utils/chargePoints/commands';
import { createSchema, type FormFields, useFormikContext } from '../../../../utils/formUtils';
import type { ChangeConfigurationStepFields, CommandsWizardState } from '../types';
import { CommandType } from '../types';

const configurationKeys: ConfigurationKey[] = [
  'APN',
  'AllowLongTermOfflineCharging',
  'AllowOfflineTxForUnknownId',
  'AuthorisationEnabled',
  'AuthorizationKey',
  'AuthorizeRemoteTxRequests',
  'AuxRelayCtrl',
  'BluetoothPassword',
  'CableLockMode',
  'ChargePointIdentity',
  'ClockAlignedDataInterval',
  'ConnectionTimeOut',
  'CpoName',
  'HeartbeatInterval',
  'LocalAuthorizeOffline',
  'LockCableUntilFinished',
  'MeterValueSampleInterval',
  'MeterValuesAlignedData',
  'MeterValuesSampledData',
  'MobileNetworkOperator',
  'OCPPEndPoint',
  'PowerSensorInstalled',
  'RFIDTagReaderEnabled',
  'ResetRetries',
  'SecurityProfile',
  'SolarStartExportPower',
  'SolarWithSchedule',
  'StopTransactionOnEVSideDisconnect',
  'StopTransactionOnInvalidId',
  'StopTxnAlignedData',
  'StopTxnSampledData',
  'SupplyCurrentLimit',
  'TransactionMessageAttempts',
  'TransactionMessageRetryInterval',
  'UnlockConnectorOnEVSideDisconnect',
  'WebSocketPingInterval',
  'WifiPassphrase',
  'WifiSSID',
];

const configurationKeyOptions = configurationKeys.map<AutocompleteContents<ConfigurationKey>>((key) => ({
  label: key,
  value: key,
}));

const fields: FormFields<ChangeConfigurationStepFields> = {
  configurationKey: {
    id: 'configurationKey',
    label: 'Key',
    inputComponent: ({
      disabled,
      error,
      helperText,
      name,
      onBlur,
      onChange,
      value,
    }: InputComponentProps<ConfigurationKey>) => (
      <AutocompleteInput<ConfigurationKey>
        disabled={disabled}
        error={error}
        helperText={helperText}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        options={configurationKeyOptions}
        useNullAsDefaultValue
        value={value}
      />
    ),
    valueComponent: ({ value }: AutocompleteContents<ConfigurationKey>) => <>{value}</>,
  },
  configurationValue: { id: 'configurationValue', label: 'Value' },
};

const validationSchema = createSchema<ChangeConfigurationStepFields>({
  configurationKey: Yup.string<ConfigurationKey>().required('Key is required'),
  configurationValue: Yup.string().required('Value is required').max(500, 'Value must be 500 characters or less'),
});

type ConfirmationDialogProps = { isOpen: boolean; setOpen: (open: boolean) => void };

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({ isOpen, setOpen }) => {
  const { handleSubmit, isSubmitting } = useFormikContext<ChangeConfigurationStepFields>();

  return (
    <Dialog hasCloseButton={false} isOpen={isOpen} onClose={() => setOpen(!isOpen)} title="Caution">
      <DialogContent>
        <Alert severity="warning">
          Incorrectly changing these variables can render charge points inoperative and unable to charge vehicles or
          permanently disable their ability to connect to our network.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={() => setOpen(false)} variant="contained">
          Cancel
        </Button>
        <Button color="primary" onClick={() => handleSubmit()} variant="contained">
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Send command'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ChangeConfigurationStep: React.FC = () => {
  const { state: untypedState, setState: untypedSetState, prevStep, nextStep, step } = useWizard();
  const state = untypedState as Partial<CommandsWizardState>;
  const savedConfig = state.command?.type === CommandType.CHANGE_CONFIGURATION ? state.command : undefined;
  const setState = untypedSetState as (state: Partial<CommandsWizardState>) => void;

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = React.useState(false);

  const onCancel = React.useCallback<OnSubmit<ChangeConfigurationStepFields>>(
    ({ configurationKey, configurationValue }) => {
      if (isConfirmationDialogOpen) {
        setConfirmationDialogOpen(false);
      } else {
        setState({ command: { type: CommandType.CHANGE_CONFIGURATION, configurationKey, configurationValue } });
        prevStep();
      }
    },
    [setState, prevStep, isConfirmationDialogOpen],
  );

  const onClickNext = React.useCallback<OnSubmit<ChangeConfigurationStepFields>>(async (_, { setSubmitting }) => {
    setSubmitting(false);
    setConfirmationDialogOpen(true);
  }, []);

  const onSubmit = React.useCallback<OnSubmit<ChangeConfigurationStepFields>>(
    ({ configurationKey, configurationValue }, { setFieldError, setSubmitting }) => {
      setSubmitting(true);
      setFieldError('configurationKey', undefined);
      setFieldError('configurationValue', undefined);
      setState({ command: { type: CommandType.CHANGE_CONFIGURATION, configurationKey, configurationValue } });
      nextStep();
    },
    [nextStep, setState],
  );

  const initialValues: ChangeConfigurationStepFields = {
    configurationKey: (savedConfig?.configurationKey ?? '') as ConfigurationKey,
    configurationValue: savedConfig?.configurationValue ?? '',
  };

  return (
    <WizardStep<ChangeConfigurationStepFields>
      fields={Object.values(fields)}
      initialValues={initialValues}
      name="Enter configuration key and value"
      onCancel={onCancel}
      onSubmit={(values, formikHelpers) =>
        isConfirmationDialogOpen ? onSubmit(values, formikHelpers) : onClickNext(values, formikHelpers)
      }
      step={step}
      subtitle="Clicking 'Next' will start updating the configuration on the charge points."
      validationSchema={validationSchema}
    >
      <ConfirmationDialog isOpen={isConfirmationDialogOpen} setOpen={setConfirmationDialogOpen} />
    </WizardStep>
  );
};
